import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion } from 'framer-motion';

const { FiZap, FiClock, FiLayers, FiTarget, FiGlobe, FiLayout, FiArrowRight } = FiIcons;

const Features = () => {
  const features = [
    {
      icon: FiZap,
      title: "AI-Powered Content in Seconds",
      description: "Generate carousels, captions, memes, and visuals instantly. No overthinking, just ideas, ready to edit and publish.",
      color: "bg-gradient-to-br from-blue-50 to-blue-100",
      iconColor: "text-blue-600",
      shadowColor: "shadow-blue-100",
      accentColor: "bg-blue-500",
      category: "AI Generation"
    },
    {
      icon: FiClock,
      title: "Save 20+ Hours Every Week",
      description: "Plan and schedule a week's content in under 30 minutes. Free up your time to focus on clients, growth, or rest.",
      color: "bg-gradient-to-br from-purple-50 to-purple-100",
      iconColor: "text-purple-600",
      shadowColor: "shadow-purple-100",
      accentColor: "bg-purple-500",
      category: "Time Saving"
    },
    {
      icon: FiLayers,
      title: "No Hassle of Tool Switching",
      description: "Write, design, schedule, and analyze in one clean dashboard. Forget juggling ChatGPT, Canva, Buffer, and Notion.",
      color: "bg-gradient-to-br from-green-50 to-green-100",
      iconColor: "text-green-600",
      shadowColor: "shadow-green-100",
      accentColor: "bg-green-500",
      category: "All-in-One"
    },
    {
      icon: FiTarget,
      title: "Your Brand, Always On Point",
      description: "Upload your logo, colors, tone, and image assets once. Every post you generate stays true to your brand.",
      color: "bg-gradient-to-br from-red-50 to-red-100",
      iconColor: "text-red-600",
      shadowColor: "shadow-red-100",
      accentColor: "bg-red-500",
      category: "Brand Consistency"
    },
    {
      icon: FiGlobe,
      title: "Manage All Major Channels",
      description: "LinkedIn, Instagram, Facebook, TikTok, Pinterest, GMB, and X. One calendar. One click. Everything tracked.",
      color: "bg-gradient-to-br from-amber-50 to-amber-100",
      iconColor: "text-amber-600",
      shadowColor: "shadow-amber-100",
      accentColor: "bg-amber-500",
      category: "Multi-Platform"
    },
    {
      icon: FiLayout,
      title: "Countless Stunning Design Templates",
      description: "Choose from high-performing post designs in just a few clicks. Quickly tailor layouts to your content and your brand look.",
      color: "bg-gradient-to-br from-indigo-50 to-indigo-100",
      iconColor: "text-indigo-600",
      shadowColor: "shadow-indigo-100",
      accentColor: "bg-indigo-500",
      category: "Design Templates"
    }
  ];

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section id="features" className="bg-white py-16 sm:py-20 lg:py-28 relative overflow-hidden">
      {/* Enhanced Background Elements */}
      <div className="absolute top-0 right-0 w-[500px] sm:w-[700px] lg:w-[900px] h-[500px] sm:h-[700px] lg:h-[900px] bg-gradient-to-l from-blue-100/30 via-purple-50/20 to-transparent rounded-full mix-blend-multiply filter blur-3xl opacity-60 translate-x-1/3 -translate-y-1/3"></div>
      <div className="absolute bottom-0 left-0 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-purple-100/30 via-blue-50/20 to-transparent rounded-full mix-blend-multiply filter blur-3xl opacity-50 -translate-x-1/3 translate-y-1/3"></div>
      <div className="absolute top-1/2 left-1/2 w-[600px] sm:w-[800px] lg:w-[1000px] h-[600px] sm:h-[800px] lg:h-[1000px] bg-gradient-to-br from-blue-50/20 to-purple-50/20 rounded-full mix-blend-multiply filter blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2"></div>

      {/* Floating decorative elements */}
      <div className="absolute top-20 left-20 w-20 h-20 bg-blue-200/30 rounded-full animate-float opacity-60"></div>
      <div className="absolute bottom-40 right-20 w-16 h-16 bg-purple-200/30 rounded-full animate-bounce-slow opacity-50"></div>
      <div className="absolute top-1/3 right-1/4 w-12 h-12 bg-green-200/30 rounded-full animate-pulse-slow opacity-40"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Enhanced Header */}
        <motion.div 
          className="text-center mb-16 sm:mb-20 lg:mb-24"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center bg-gradient-to-r from-primary/10 to-purple-500/10 text-primary px-6 py-3 rounded-full text-sm font-semibold mb-8 backdrop-blur-sm border border-primary/20 shadow-lg">
            <SafeIcon icon={FiZap} className="mr-2" />
            What You Get
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-primary leading-tight">
            Everything You Need in <span className="font-extrabold">One Place</span>
          </h2>
          <p className="text-lg sm:text-xl text-text-light max-w-4xl mx-auto leading-relaxed px-4">
            A complete toolkit to create, schedule, and manage your social content effortlessly
          </p>
        </motion.div>

        {/* Enhanced Features Grid */}
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10 px-4 sm:px-0 mb-20 sm:mb-24"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="group relative"
              variants={item}
              whileHover={{ y: -8 }}
              transition={{ duration: 0.3 }}
            >
              {/* Card Background with enhanced gradient */}
              <div className={`${feature.color} rounded-3xl p-8 sm:p-10 h-full border border-gray-200/50 relative overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 backdrop-blur-sm`}>
                {/* Background pattern */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-white/20 opacity-60"></div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/20 rounded-full -translate-x-8 -translate-y-16 filter blur-xl opacity-50"></div>

                {/* Category badge */}
                <div className="absolute top-4 right-4">
                  <div className={`${feature.accentColor} text-white text-xs font-bold px-3 py-1 rounded-full shadow-md`}>
                    {feature.category}
                  </div>
                </div>

                <div className="relative z-10">
                  {/* Enhanced Icon */}
                  <div className={`w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-2xl sm:rounded-3xl flex items-center justify-center mb-6 sm:mb-8 group-hover:scale-110 transition-all duration-300 shadow-xl border border-gray-100`}>
                    <SafeIcon icon={feature.icon} className={`${feature.iconColor} text-2xl sm:text-3xl`} />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl sm:text-2xl font-bold text-text mb-4 sm:mb-6 group-hover:text-primary transition-colors leading-tight">
                    {feature.title}
                  </h3>
                  <p className="text-text-light leading-relaxed text-base sm:text-lg mb-6 sm:mb-8">
                    {feature.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Features;